import React, { ReactNode } from 'react';

import SearchableMenu from 'components/base/Menu/SearchableMenu';
import Checkbox from 'components/base/Checkbox/Checkbox';

import Span from 'components/elements/span';
import Box from 'components/elements/Box';
import colors from 'styles/themes/colors';
import CountryFlag from '../../CountryFlag';
import { RecentFilter } from 'components/TopicFilters/TopicFilters.styles';

import { findString } from 'helpers/findString';
import styled, { css } from 'styled-components/macro';
import { ThemeProps } from 'types/styled';
import variables from 'styles/themes/variables';

import DropdownWrapper from '../DropdownWrapper';
import DropdownButton from '../DropdownButton';
import { ListLabelSpan } from '../shared/styles';

const hoveredStyles = (): any => css`
  background: ${colors.Sand};
`;

export const ListItemBox = styled(Box)<ThemeProps<'div'>>`
  ${({ isActive, isHovered }): any => css`
    cursor: pointer;
    background: ${isActive || isHovered ? colors.Sand : 'white'};

    span {
      display: inline-block;
      transition: ${`150ms ${variables.timingFunction} transform`};
    }

    &:hover,
    &[aria-selected='true'] {
      ${hoveredStyles()}
    }

    ${isHovered && hoveredStyles()}
  `}
`;

export const ListItem: React.FC<IListItemProps> = (props) => {
  const { item, isActive, isHovered, isDisplayedWithFlag } = props;

  return item ? (
    <ListItemBox
      isActive={false}
      isHovered={isHovered}
      display="flex"
      alignItems="flex-start"
      justifyContent="flex-start"
      alignContent="stretch"
      paddingRight={2}
      paddingLeft={3}
      py={2}
    >
      <Box
        alignSelf="center"
        display="inline-block"
        marginLeft="2px"
        marginRight="-2px"
      >
        <Checkbox enabledText="Select" isChecked={!!isActive} insideDropdown />
      </Box>
      <Box alignSelf="auto" display="inline-block">
        {isDisplayedWithFlag && (
          <CountryFlag
            small
            id={item.id ?? 'WW'}
            displayName={item.displayName}
            flagImgSmall={item.flagImgSmall}
            flagImgMedium={item.flagImgMedium}
            flagImgLarge={item.flagImgLarge}
          />
        )}
      </Box>
      <Span marginLeft={1} alignSelf="start" textAlign="left">
        {item.label}
      </Span>
      <RecentFilter>
        {item.isRecent && !isActive ? (item.isEx ? 'EX' : 'IN') : ''}
      </RecentFilter>
    </ListItemBox>
  ) : null;
};

function ListLabel({ isExclude }: any): ReactNode {
  return (
    <Span
      marginLeft={3}
      alignSelf="auto"
      color={colors.CasperGrey}
      fontSize={13}
      letterSpacing={0.5}
      fontWeight={600}
      display="inline-flex"
      paddingTop={3}
    >
      {isExclude ? 'EXCLUDE COUNTRY' : 'SELECT COUNTRY'}
    </Span>
  );
}

const getButtonWithDefaultLabel = (
  clearFilter?: () => void,
  defaultLabel?: string
): React.FC<any> => {
  const LabeledButton: React.FC<any> = (props: any) => {
    return (
      <DropdownButton
        {...props}
        defaultLabel={defaultLabel}
        dropdownType="Territory"
        clearFilter={clearFilter}
      />
    );
  };
  return LabeledButton;
};

const ExtendedCountryDropdown: React.FC<IExtendedCountryDropdownProps> = (
  props
) => {
  const {
    items,
    selectedItem,
    selectedItems,
    onChange,
    disabled,
    itemToString,
    defaultLabel,
    children,
    isDisplayedWithFlag = true,
    isOpened = false,
    onClose,
    isExclude = false,
    disabledMessage,
    showAllItems,
    isMultipleSelection,
    selectedItemsIds,
    clearFilter,
  } = props;
  const matchByCountryId = (item: any, searchValue: string): boolean => {
    return findString(searchValue)(item.id);
  };

  // selectedItem can be undefined which will trigger React into thinking this is a controlled component,
  // when it then switches to an actual value runtime errors will be thrown since React will figure
  // we're trying to switch a conrtolled component into an uncontrolled component. Null is a valid value,
  // therefore we add this seemingly weird check.
  return (
    <DropdownWrapper label="TERRITORY">
      <SearchableMenu
        onClose={onClose}
        items={items}
        selectedItem={selectedItem}
        selectedItems={selectedItemsIds}
        onChange={onChange}
        disabled={disabled}
        itemToString={itemToString}
        Button={getButtonWithDefaultLabel(clearFilter, defaultLabel)}
        Item={ListItem}
        additionalFindMethod={matchByCountryId}
        isDisplayedWithFlag={isDisplayedWithFlag}
        showOnlyRecent={!isOpened}
        isOpenDropdownDef={isOpened}
        hideRecentBlock={isOpened}
        ListLabel={isOpened ? ListLabel : ListLabelSpan}
        isExclude={isExclude}
        showListLabel={isOpened}
        disabledMessage={disabledMessage}
        showAllItems={showAllItems}
        multipleSelections={selectedItems}
        isMultipleSelection={isMultipleSelection}
      >
        {children}
      </SearchableMenu>
    </DropdownWrapper>
  );
};

interface IExtendedCountryDropdownProps extends ICountryDropdownProps {
  isOpened?: boolean;
  onClose: () => void;
  isExclude?: boolean;
  disabledMessage?: string;
  showOnlyRecent?: boolean;
  showAllItems?: boolean;
  selectedItems?: any;
  selectedItemsIds?: any;
  isMultipleSelection?: boolean;
  clearFilter: () => void;
}

interface ICountryDropdownProps {
  items: any[];
  selectedItem?: any;
  onChange: (item: any) => void;
  disabled?: boolean;
  disabledMessage?: string;
  itemToString?: (item: any) => string | null;
  defaultLabel?: string;
  children?: any;
  isDisplayedWithFlag?: boolean;
  /** Max width of the tooltip for the dropdown, zIndex of the tooltip block,
   * placement is for place of tooltip relatively the block, isStretched is to stretch the block in tooltip wrapper */
  maxWidth?: string;
  placement?: string;
  zIndex?: string;
  isStretched?: boolean;
}

interface IListItemProps {
  item: any;
  isActive?: boolean;
  isHovered?: boolean;
  isDisplayedWithFlag?: boolean;
  disableCheckbox?: boolean;
}

export default ExtendedCountryDropdown;
